import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import {
  Box,
  IconButton,
  Divider,
  Tooltip,
  Select,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import {
  TextStyle,
  Color,
  FontFamily,
  FontSize,
} from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
};

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
}: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      FontSize,
      FontFamily,
      Highlight.configure({ multicolor: true }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer",
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Sync external value changes (e.g. when editing loads existing content)
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "");
    }
  }, [value]);

  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt("Enter URL (e.g. https://google.com):");
    if (!url) return;

    // Auto-add https:// if no protocol specified
    const href =
      url.startsWith("http://") ||
      url.startsWith("https://") ||
      url.startsWith("/") // internal links like /blogs/my-post
        ? url
        : `https://${url}`;

    editor.chain().focus().setLink({ href }).run();
  };

  const ToolbarButton = ({
    onClick,
    active,
    title,
    children,
  }: {
    onClick: () => void;
    active?: boolean;
    title: string;
    children: React.ReactNode;
  }) => (
    <Tooltip title={title} arrow>
      <IconButton
        onMouseDown={(e) => {
          e.preventDefault();
          onClick();
        }}
        size="small"
        sx={{
          borderRadius: 1,
          color: active ? "#fff" : "#333",
          bgcolor: active ? "#111" : "transparent",
          "&:hover": {
            bgcolor: active ? "#333" : "rgba(0,0,0,0.06)",
          },
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );

  return (
    <Box
      sx={{
        border: "1px solid rgba(0,0,0,0.23)",
        borderRadius: 1,
        overflow: "hidden",
        "&:focus-within": {
          borderColor: "#1976d2",
          boxShadow: "0 0 0 2px rgba(25,118,210,0.15)",
        },
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          bgcolor: "#fafafa",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          p: 0.75,
          display: "flex",
          flexWrap: "wrap",
          gap: 0.25,
          alignItems: "center",
        }}
      >
        {/* Heading selector */}
        <Select
          size="small"
          value={
            editor.isActive("heading", { level: 1 })
              ? "h1"
              : editor.isActive("heading", { level: 2 })
                ? "h2"
                : editor.isActive("heading", { level: 3 })
                  ? "h3"
                  : "p"
          }
          onChange={(e) => {
            const size = e.target.value;
            const val = e.target.value;
            if (size) {
              editor.chain().focus().setFontSize(size).run();
            }
            if (val === "p") {
              editor.chain().focus().setParagraph().run();
            } else {
              const level = parseInt(val.replace("h", "")) as 1 | 2 | 3;
              editor.chain().focus().toggleHeading({ level }).run();
            }
          }}
          sx={{
            fontSize: 13,
            height: 30,
            minWidth: 110,
            ".MuiSelect-select": { py: 0.5, px: 1 },
          }}
        >
          <MenuItem value="p" sx={{ fontSize: 13 }}>
            Paragraph
          </MenuItem>
          <MenuItem value="h1" sx={{ fontSize: 13, fontWeight: 700 }}>
            Heading 1
          </MenuItem>
          <MenuItem value="h2" sx={{ fontSize: 13, fontWeight: 700 }}>
            Heading 2
          </MenuItem>
          <MenuItem value="h3" sx={{ fontSize: 13, fontWeight: 700 }}>
            Heading 3
          </MenuItem>
        </Select>

        {/* Font Size */}
        <Select
          size="small"
          value=""
          displayEmpty
          onChange={(e) => {
            const size = e.target.value;
            if (size) {
              editor
                .chain()
                .focus()
                .setMark("textStyle", { fontSize: size })
                .run();
            }
          }}
          sx={{
            fontSize: 13,
            height: 30,
            minWidth: 90,
            ".MuiSelect-select": { py: 0.5, px: 1 },
          }}
          renderValue={() => "Font Size"}
        >
          <MenuItem value="12px" sx={{ fontSize: 12 }}>
            Small (12px)
          </MenuItem>
          <MenuItem value="14px" sx={{ fontSize: 14 }}>
            Normal (14px)
          </MenuItem>
          <MenuItem value="16px" sx={{ fontSize: 16 }}>
            Medium (16px)
          </MenuItem>
          <MenuItem value="20px" sx={{ fontSize: 20 }}>
            Large (20px)
          </MenuItem>
          <MenuItem value="24px" sx={{ fontSize: 24 }}>
            XL (24px)
          </MenuItem>
          <MenuItem value="32px" sx={{ fontSize: 32 }}>
            XXL (32px)
          </MenuItem>
        </Select>

        {/* Font Family */}
        <Select
          size="small"
          value=""
          displayEmpty
          onChange={(e) => {
            const font = e.target.value;
            if (font) editor.chain().focus().setFontFamily(font).run();
          }}
          sx={{
            fontSize: 13,
            height: 30,
            minWidth: 100,
            ".MuiSelect-select": { py: 0.5, px: 1 },
          }}
          renderValue={() => "Font"}
        >
          <MenuItem
            value="Inter, sans-serif"
            sx={{ fontFamily: "Inter, sans-serif" }}
          >
            Inter
          </MenuItem>
          <MenuItem
            value='"Bodoni Moda", serif'
            sx={{ fontFamily: '"Bodoni Moda", serif' }}
          >
            Bodoni Moda
          </MenuItem>
          <MenuItem
            value='"Playfair Display", serif'
            sx={{ fontFamily: '"Playfair Display", serif' }}
          >
            Playfair Display
          </MenuItem>
          <MenuItem
            value="Georgia, serif"
            sx={{ fontFamily: "Georgia, serif" }}
          >
            Georgia
          </MenuItem>
          <MenuItem
            value="Arial, sans-serif"
            sx={{ fontFamily: "Arial, sans-serif" }}
          >
            Arial
          </MenuItem>
          <MenuItem value="monospace" sx={{ fontFamily: "monospace" }}>
            Monospace
          </MenuItem>
        </Select>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Text formatting */}
        <ToolbarButton
          title="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <FormatBoldIcon fontSize="small" />
        </ToolbarButton>

        <ToolbarButton
          title="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <FormatItalicIcon fontSize="small" />
        </ToolbarButton>

        <ToolbarButton
          title="Underline"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <FormatUnderlinedIcon fontSize="small" />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Alignment */}
        <ToolbarButton
          title="Align Left"
          active={editor.isActive({ textAlign: "left" })}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
        >
          <FormatAlignLeftIcon fontSize="small" />
        </ToolbarButton>

        <ToolbarButton
          title="Align Center"
          active={editor.isActive({ textAlign: "center" })}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
        >
          <FormatAlignCenterIcon fontSize="small" />
        </ToolbarButton>

        <ToolbarButton
          title="Align Right"
          active={editor.isActive({ textAlign: "right" })}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
        >
          <FormatAlignRightIcon fontSize="small" />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Lists */}
        <ToolbarButton
          title="Bullet List"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <FormatListBulletedIcon fontSize="small" />
        </ToolbarButton>

        <ToolbarButton
          title="Numbered List"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <FormatListNumberedIcon fontSize="small" />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Quote and Rule */}
        <ToolbarButton
          title="Blockquote"
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <FormatQuoteIcon fontSize="small" />
        </ToolbarButton>

        <ToolbarButton
          title="Horizontal Rule"
          active={false}
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          <HorizontalRuleIcon fontSize="small" />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Link */}
        <ToolbarButton
          title="Add Link"
          active={editor.isActive("link")}
          onClick={addLink}
        >
          <LinkIcon fontSize="small" />
        </ToolbarButton>

        <ToolbarButton
          title="Remove Link"
          active={false}
          onClick={() => editor.chain().focus().unsetLink().run()}
        >
          <LinkOffIcon fontSize="small" />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Undo / Redo */}
        <ToolbarButton
          title="Undo"
          active={false}
          onClick={() => editor.chain().focus().undo().run()}
        >
          <UndoIcon fontSize="small" />
        </ToolbarButton>

        <ToolbarButton
          title="Redo"
          active={false}
          onClick={() => editor.chain().focus().redo().run()}
        >
          <RedoIcon fontSize="small" />
        </ToolbarButton>

        {/* Text Color */}
        <Tooltip title="Text Color" arrow>
          <Box
            sx={{ display: "flex", alignItems: "center", position: "relative" }}
          >
            <IconButton size="small" sx={{ borderRadius: 1 }} component="label">
              <FormatColorTextIcon fontSize="small" />
              <input
                type="color"
                style={{
                  position: "absolute",
                  opacity: 0,
                  width: "100%",
                  height: "100%",
                  cursor: "pointer",
                }}
                onChange={(e) => {
                  editor.chain().focus().setColor(e.target.value).run();
                }}
              />
            </IconButton>
          </Box>
        </Tooltip>

        {/* Highlight Color */}
        <Tooltip title="Highlight" arrow>
          <Box
            sx={{ display: "flex", alignItems: "center", position: "relative" }}
          >
            <IconButton size="small" sx={{ borderRadius: 1 }} component="label">
              <BorderColorIcon fontSize="small" />
              <input
                type="color"
                defaultValue="#FFFF00"
                style={{
                  position: "absolute",
                  opacity: 0,
                  width: "100%",
                  height: "100%",
                  cursor: "pointer",
                }}
                onChange={(e) => {
                  editor
                    .chain()
                    .focus()
                    .setHighlight({ color: e.target.value })
                    .run();
                }}
              />
            </IconButton>
          </Box>
        </Tooltip>

        {/* Clear Formatting */}
        <Tooltip title="Clear Formatting" arrow>
          <IconButton
            size="small"
            sx={{ borderRadius: 1 }}
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().unsetAllMarks().clearNodes().run();
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700, px: 0.5 }}>
              Clear
            </Typography>
          </IconButton>
        </Tooltip>
      </Box>

      {/* Editor Content Area */}
      <Box
        sx={{
          p: 2,
          minHeight: 320,
          bgcolor: "#fff",
          "& .ProseMirror": {
            outline: "none",
            minHeight: 300,
            fontSize: "0.95rem",
            lineHeight: 1.8,
            color: "#111",
            "& p": { margin: "0 0 12px 0" },
            "& h1": {
              fontSize: "2rem",
              fontWeight: 700,
              margin: "24px 0 12px",
            },
            "& [style*='font-size']": { fontSize: "inherit" },
            "& mark": {
              borderRadius: "2px",
              padding: "0 2px",
            },
            "& h2": {
              fontSize: "1.5rem",
              fontWeight: 700,
              margin: "20px 0 10px",
            },
            "& h3": {
              fontSize: "1.2rem",
              fontWeight: 700,
              margin: "16px 0 8px",
            },
            "& ul, & ol": { paddingLeft: "24px", margin: "0 0 12px 0" },
            "& li": { marginBottom: "4px" },
            "& blockquote": {
              borderLeft: "3px solid #111",
              paddingLeft: "16px",
              margin: "16px 0",
              color: "rgba(0,0,0,0.6)",
              fontStyle: "italic",
            },
            "& a": {
              color: "#1976d2",
              textDecoration: "underline",
              cursor: "pointer",
            },
            "& hr": {
              border: "none",
              borderTop: "1px solid rgba(0,0,0,0.12)",
              margin: "24px 0",
            },
            "& p.is-editor-empty:first-of-type::before": {
              content: `"${placeholder || "Write your blog content here..."}"`,
              color: "rgba(0,0,0,0.38)",
              pointerEvents: "none",
              float: "left",
              height: 0,
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}
